<template>
  <div>
    <ib-autocomplete v-model="template" outlined :items="templates" item-text="name" return-object no-data-text="Não há templates disponíveis" item-value="name" :label="label" hide-details="auto" @change="templateChanged" />

    <div v-if="template">
      <div v-if="header_media" class="mt-4">
        <div class="text-subtitle-2 mb-2">{{ header_media_title }}</div>

        <div class="d-flex align-center flex-wrap">
          <ib-btn color="primary" outlined @click="mediaDialog = true">
            <v-icon size="18" class="mr-2">mdi-folder-multiple-image</v-icon>
            {{ header_media.link ? 'Trocar arquivo' : 'Selecionar arquivo' }}
          </ib-btn>

          <div v-if="header_media.link" class="d-flex align-center ml-3">
            <v-img v-if="header_media.format == 'IMAGE'" :src="header_media.link" width="70" height="70" contain class="rounded" />

            <video v-else-if="header_media.format == 'VIDEO'" :src="header_media.link" width="140" controls class="rounded" />

            <v-icon v-else size="40">mdi-file-document-outline</v-icon>

            <span class="caption gray600--text ml-2">{{ header_media.name }}</span>
          </div>
        </div>
      </div>

      <v-row v-if="parameters.length" class="mt-1">
        <v-col cols="12">
          <div class="text-subtitle-2 mb-1">Variáveis do template</div>
          <div class="caption gray600--text">Informe o valor que substituirá cada variável no envio</div>
        </v-col>

        <v-col v-for="par in parameters" :key="`${par.location}_${par.index}_${par.par}`" cols="12" sm="6">
          <ib-text-field v-model="par.value" outlined hide-details="auto" :label="parameterLabel(par)" placeholder="Valor da variável" @input="emitChange" />
        </v-col>
      </v-row>

      <div class="mt-4">
        <div class="text-subtitle-2 mb-2">Pré-visualização</div>

        <div class="template-preview ib-scroll">
          <div v-if="header_media" class="template-preview-media">
            <v-img v-if="header_media.format == 'IMAGE' && header_media.link" :src="header_media.link" height="140" class="rounded" />

            <video v-else-if="header_media.format == 'VIDEO' && header_media.link" :src="header_media.link" class="template-preview-video rounded" controls />

            <div v-else class="template-preview-media-empty">
              <v-icon size="28">{{ header_media_icon }}</v-icon>
              <span class="caption ml-2">{{ header_media.link ? header_media.name : 'Nenhum arquivo selecionado' }}</span>
            </div>
          </div>

          <div v-if="preview.header_text" class="template-preview-header">{{ preview.header_text }}</div>

          <div class="template-preview-body">{{ preview.body_text }}</div>

          <div v-if="preview.footer_text" class="template-preview-footer">{{ preview.footer_text }}</div>

          <div v-for="(btn, index) in preview.buttons" :key="`preview_button_${index}`" class="template-preview-button">{{ btn }}</div>
        </div>
      </div>
    </div>

    <dialog-medias v-model="mediaDialog" :medias="medias" :update-medias="updateMedias" :send-media="selectHeaderMedia" :types="header_media_types" action-label="Usar arquivo" />
  </div>
</template>

<script>
import DialogMedias from '@/components/DialogMedias';

const PARAMETER_REGEX = /\{\{\s*(\d+)\s*\}\}/g;

export default {
  name: 'TemplateComposer',

  components: {
    DialogMedias,
  },

  props: {
    value: Object,
    templates: Array,
    medias: Array,
    updateMedias: Function,
    label: {
      type: String,
      default: 'Template de mensagem'
    },
  },

  data() {
    return {
      template: null,
      parameters: [],
      header_media: null,
      mediaDialog: false,
    };
  },

  computed: {
    header_media_types() {
      if (!this.header_media) return [];
      return [this.header_media.format.toLowerCase()];
    },

    header_media_title() {
      if (!this.header_media) return '';
      if (this.header_media.format == 'IMAGE') return 'Imagem do cabeçalho';
      if (this.header_media.format == 'VIDEO') return 'Vídeo do cabeçalho';
      return 'Documento do cabeçalho';
    },

    header_media_icon() {
      if (!this.header_media) return 'mdi-file-document-outline';
      if (this.header_media.format == 'IMAGE') return 'mdi-image-outline';
      if (this.header_media.format == 'VIDEO') return 'mdi-video-outline';
      return 'mdi-file-document-outline';
    },

    preview() {
      const components = this.template?.components || [];
      const header = components.find(c => c.type == 'HEADER');
      const body = components.find(c => c.type == 'BODY');
      const footer = components.find(c => c.type == 'FOOTER');
      const buttons = components.find(c => c.type == 'BUTTONS')?.buttons || [];

      return {
        header_text: this.renderText(header?.format == 'TEXT' || !header?.format ? header?.text : '', 'header'),
        body_text: this.renderText(body?.text, 'body'),
        footer_text: footer?.text || '',
        buttons: buttons.map(b => this.renderButtonText(b))
      };
    },

    is_valid() {
      if (!this.template) return false;
      if (this.header_media && !this.header_media.link) return false;
      return this.parameters.every(p => String(p.value || '').trim() !== '');
    },
  },

  watch: {
    value(val) {
      if (!val) this.reset();
    },

    templates() {
      if (this.template && !this.templates?.some(t => t.name == this.template.name)) this.reset(true);
    },
  },

  methods: {
    reset(emit = false) {
      this.template = null;
      this.parameters = [];
      this.header_media = null;
      if (emit) this.$emit('input', null);
    },

    extractParameters(text) {
      const found = [];
      for (const match of String(text || '').matchAll(PARAMETER_REGEX)) {
        const number = Number(match[1]);
        if (!found.includes(number)) found.push(number);
      }
      return found.sort((a, b) => a - b);
    },

    templateChanged() {
      this.parameters = [];
      this.header_media = null;

      if (!this.template) return this.$emit('input', null);

      const components = this.template.components || [];

      //CABEÇALHO
      const header = components.find(c => c.type == 'HEADER');

      if (header) {
        if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
          this.header_media = { format: header.format, link: '', name: '' };
        }
        else {
          this.extractParameters(header.text).forEach(par => this.parameters.push({ location: 'header', par: par, index: 0, value: '' }));
        }
      }

      //CORPO
      const body = components.find(c => c.type == 'BODY');
      this.extractParameters(body?.text).forEach(par => this.parameters.push({ location: 'body', par: par, index: 0, value: '' }));

      //BOTÕES COM URL DINÂMICA
      const buttons = components.find(c => c.type == 'BUTTONS')?.buttons || [];
      buttons.forEach((btn, index) => {
        if (btn.type != 'URL') return;
        this.extractParameters(btn.url).forEach(par => this.parameters.push({ location: 'button', par: par, index: index, value: '' }));
      });

      this.emitChange();
    },

    parameterLabel(par) {
      const locations = { header: 'cabeçalho', body: 'corpo', button: 'botão' };
      return `Variável ${par.par} (${locations[par.location]})`;
    },

    renderText(text, location) {
      if (!text) return '';

      return String(text).replace(PARAMETER_REGEX, (match, number) => {
        const par = this.parameters.find(p => p.location == location && p.par == Number(number));
        return par && String(par.value).trim() !== '' ? par.value : match;
      });
    },

    renderButtonText(button) {
      if (button.type == 'URL') return `🔗 ${button.text}`;
      if (button.type == 'PHONE_NUMBER') return `📞 ${button.text}`;
      return button.text;
    },

    selectHeaderMedia(media) {
      this.mediaDialog = false;
      this.header_media.link = media.url;
      this.header_media.name = media.name;
      this.emitChange();
    },

    buildComponents() {
      const components = [];

      //CABEÇALHO
      const header_parameters = [];

      if (this.header_media?.link) {
        if (this.header_media.format == 'IMAGE') header_parameters.push({ type: 'image', image: { link: this.header_media.link } });
        if (this.header_media.format == 'VIDEO') header_parameters.push({ type: 'video', video: { link: this.header_media.link } });
        if (this.header_media.format == 'DOCUMENT') header_parameters.push({ type: 'document', document: { link: this.header_media.link, filename: this.header_media.name } });
      }

      this.parameters.filter(p => p.location == 'header').forEach(p => header_parameters.push({ type: 'text', text: String(p.value) }));
      if (header_parameters.length) components.push({ type: 'header', parameters: header_parameters });

      //CORPO
      const body_parameters = this.parameters.filter(p => p.location == 'body').map(p => ({ type: 'text', text: String(p.value) }));
      if (body_parameters.length) components.push({ type: 'body', parameters: body_parameters });

      //BOTÕES COM URL DINÂMICA
      const button_parameters = this.parameters.filter(p => p.location == 'button');
      button_parameters.forEach(p => components.push({ type: 'button', sub_type: 'url', index: p.index, parameters: [{ type: 'text', text: String(p.value) }] }));

      return components;
    },

    emitChange() {
      if (!this.template) return this.$emit('input', null);

      this.$emit('input', {
        template: this.template,
        template_components: this.buildComponents(),
        valid: this.is_valid
      });
    },
  },
};
</script>

<style scoped>
.template-preview {
  background-color: var(--v-gray50-base);
  border: 1px solid var(--v-gray200-base);
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  color: var(--v-gray1000-base);
  max-height: 320px;
  overflow-y: auto;
}

.template-preview-media {
  margin-bottom: 8px;
}

.template-preview-video {
  width: 100%;
  max-height: 180px;
}

.template-preview-media-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  border: 1px dashed var(--v-gray400-base);
  border-radius: 4px;
  color: var(--v-gray500-base);
}

.template-preview-header {
  font-weight: 700;
  margin-bottom: 6px;
  white-space: pre-wrap;
}

.template-preview-body {
  white-space: pre-wrap;
}

.template-preview-footer {
  margin-top: 8px;
  font-size: 12px;
  color: var(--v-gray500-base);
  white-space: pre-wrap;
}

.template-preview-button {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--v-gray200-base);
  text-align: center;
  color: #0a7cff;
}

/* Dark mode */
.theme--dark .template-preview-button {
  color: #4da3ff;
}
</style>
